import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Plus, Eye } from "lucide-react";
import { SidebarContent } from "@/components/layout/Sidebar";
import { Header } from "@/components/layout/Header";
import { ProductTour } from "@/components/ProductTour";
import { ForcePasswordChange } from "@/components/ForcePasswordChange";
import { AIChatWidget } from "@/components/AIChatWidget";
import { WinShoutout } from "@/components/WinShoutout";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("uv_sidebar_collapsed") === "1");
  const setCollapse = (v) => { setCollapsed(v); localStorage.setItem("uv_sidebar_collapsed", v ? "1" : "0"); };
  const { user, exitImpersonation } = useAuth();
  const navigate = useNavigate();
  const canQuickAdd = ["super_admin", "sales_head", "region_head", "sales_manager", "salesperson", "bd", "sc", "sales_coordinator", "dealer"].includes(user?.role);

  useEffect(() => {
    if (!user) return;
    if (user.role === "bd") return; // BD lands on a focused KRA tracker; the sales-dashboard tour would block it
    if (user.impersonated) return; // no tour while viewing someone else's account
    if (!localStorage.getItem("uv_tour_v1") && window.innerWidth >= 768) {
      const t = setTimeout(() => setTourOpen(true), 900);
      return () => clearTimeout(t);
    }
  }, [user]);

  const closeTour = () => { setTourOpen(false); localStorage.setItem("uv_tour_v1", "1"); };

  useEffect(() => {
    if (!user || user.impersonated) return;
    const ping = () => { if (document.visibilityState === "visible") api.post("/team-activity/ping").catch(() => {}); };
    ping();
    const t = setInterval(ping, 120000);
    return () => clearInterval(t);
  }, [user?.id, user?.impersonated]);

  if (user && user.must_change_password && !user.impersonated) return <ForcePasswordChange />;

  return (
    <div className={`min-h-screen flex bg-[#F9F9FA] ${user?.impersonated ? "pt-9" : ""}`}>
      {user?.impersonated && (
        <div className="fixed top-0 inset-x-0 z-[60] bg-amber-400 text-slate-900 text-sm flex items-center justify-center gap-2 px-4 py-1.5 no-print" data-testid="impersonation-banner">
          <Eye className="h-4 w-4 shrink-0" />
          <span className="truncate">Viewing as <b>{user.name}</b> — you see exactly what they see.</span>
          <button onClick={exitImpersonation} className="underline font-bold hover:opacity-80 whitespace-nowrap" data-testid="exit-impersonation-btn">Return to my account</button>
        </div>
      )}

      <aside className={`hidden md:block ${collapsed ? "w-[68px]" : "w-64"} flex-shrink-0 sticky top-0 h-screen no-print transition-[width] duration-200`}>
        <SidebarContent collapsed={collapsed} onToggleCollapse={setCollapse} />
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-64 border-0" style={{ paddingTop: "env(safe-area-inset-top)" }}>
          <SidebarContent onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>

      <div className="flex-1 min-w-0 flex flex-col">
        <Header onMenu={() => setMobileOpen(true)} onStartTour={() => setTourOpen(true)} />
        <main className="flex-1 p-4 sm:p-6 lg:p-8 fade-in">
          <Outlet />
        </main>
      </div>

      <ProductTour open={tourOpen} onClose={closeTour} />

      <AIChatWidget />
      <WinShoutout />

      {canQuickAdd && (
        <button
          onClick={() => navigate("/leads?new=1")}
          className="md:hidden fixed right-6 h-14 w-14 rounded-full bg-slate-900 text-white shadow-lg flex items-center justify-center z-40 active:scale-95 transition-transform"
          style={{ bottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}
          data-testid="mobile-quick-add"
        >
          <Plus className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}
